import { Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { CONTRACT_STATUS_ENUM, DocUploadedResponse, FILE_TYPE_ENUM, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { useStores } from "stores";
import { EditableFile, FileInput, WhiteBoxBlock } from "ui";
import { removeContractFile, uploadContractFile } from "services";
import { isStatusIncluded, parseErrorMessage, getTotalDocumentsCount } from "utils";
import { useNotificationContext } from "providers";

export const ContractFilesBlock = observer(() => {
  const { contractCard, user } = useStores();
  const { openNotification } = useNotificationContext();

  const { isCustomer, isJuniorOperator, isSeniorOperator, isSeniorCustomer } = user.roles;

  const isOperator = isJuniorOperator || isSeniorOperator;

  const [fileLoading, setFileLoading] = useState(false);

  const { id, documents, status } = contractCard.card;

  const fileOnFinishHandler = () => {
    setTimeout(() => {
      setFileLoading(false);
      contractCard.updateCardById(id);
    }, 1500);
  };

  const handleUploadFile = async (responseUpload: DocUploadedResponse[]) => {
    setFileLoading(true);
    await uploadContractFile(
      { contractId: id },
      { docIds: responseUpload.map((item) => item.id) }
    )
      .then(() => {
        fileOnFinishHandler();
      })
      .catch((e) => {
        setFileLoading(false);
        throw e;
      });
  };

  const handleRemoveFile = async (fileId: string) => {
    setFileLoading(true);
    return await removeContractFile({ contractId: id, documentId: fileId })
      .then(() => {
        fileOnFinishHandler();
      })
      .catch((e) => {
        setFileLoading(false);
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      });
  };

  const showFileInput =
    (isCustomer && status === CONTRACT_STATUS_ENUM.DRAFT) ||
    (isOperator && isStatusIncluded(status, [CONTRACT_STATUS_ENUM.DRAFT, CONTRACT_STATUS_ENUM.ACTIVE]));

  const showFilesBlock = documents[FILE_TYPE_ENUM.CONTRACT] || showFileInput;

  const totalDocumentsCount = getTotalDocumentsCount(documents);

  return (
    <>
      {showFilesBlock && (
        <WhiteBoxBlock>
          <Spin spinning={fileLoading}>
            <Flex vertical gap="small">
              <Flex vertical gap="small" id="contract-documents">
                <Flex gap={4} align="center" className="margin-bottom-md">
                  <Typography.Text className="whitebox-title">Документы</Typography.Text>
                  {totalDocumentsCount > 0 && <Typography.Text strong>({totalDocumentsCount})</Typography.Text>}
                </Flex>
                {documents[FILE_TYPE_ENUM.CONTRACT]?.length > 0 &&
                  documents[FILE_TYPE_ENUM.CONTRACT].map((item) => (
                    <EditableFile
                      key={item.id}
                      tooltip={"Договор"}
                      showRemoveIcon={(isSeniorCustomer || isSeniorOperator) && status === CONTRACT_STATUS_ENUM.DRAFT}
                      file={item}
                      removeFileHandler={() => handleRemoveFile(item.id)}
                    />
                  ))}
              </Flex>
              {showFileInput && (
                <div className="margin-top-md">
                  <FileInput
                    fileType={FILE_TYPE_ENUM.CONTRACT}
                    onUpload={handleUploadFile}
                    blockTitle="Документ (скан договора)"
                    multiple
                    isContinue={documents[FILE_TYPE_ENUM.CONTRACT]?.length > 0}
                  />
                </div>
              )}
            </Flex>
          </Spin>
        </WhiteBoxBlock>
      )}
    </>
  );
});
